/**
 * RosterTable — a project's employees as a DataTable (demo /manage-team members table, /tables/data-tables).
 * The employee's name opens the profile; the whole row is clickable as well.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { DataTable, type DataTableColumn } from '../../../components/ui/index.js';
import { Avatar, StatusBadge, PerfBar, perfTone, type EmployeeView } from '../shared';

interface RosterTableProps {
  employees: EmployeeView[];
  onOpen: (id: string) => void;
  /** Where an employee's profile lives; the name links there. */
  hrefFor: (id: string) => string;
  projectName: string;
  linkedEmployeeIds: Map<string, unknown>;
  loading?: boolean;
  empty?: React.ReactNode;
  /** Rows per page; omit to show every row. */
  pageSize?: number;
}

export const RosterTable: React.FC<RosterTableProps> = ({
  employees,
  onOpen,
  hrefFor,
  projectName,
  linkedEmployeeIds,
  loading,
  empty,
  pageSize,
}) => {
  const columns: DataTableColumn<EmployeeView>[] = [
    {
      key: 'employee',
      header: 'Employee',
      sortable: true,
      sortValue: (emp) => emp.name.toLowerCase(),
      render: (emp) => (
        <div className="flex items-center gap-3">
          <Avatar label={emp.name} kind={emp.kind} initials={emp.initials} size={32} showKind />
          <div className="min-w-0">
            <Link
              to={hrefFor(emp.id)}
              onClick={(e) => e.stopPropagation()}
              className="block truncate font-medium text-heading hover:underline"
            >
              {emp.name}
            </Link>
            <div className="truncate text-xs text-muted">
              {emp.role}
              {emp.tier ? ` · ${emp.tier}` : null}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'project',
      header: 'Project / team',
      render: (emp) => {
        const linked = linkedEmployeeIds.has(emp.id);
        return (
          <div className="text-xs">
            <div className="text-body">{linked ? projectName : 'Reusable employee'}</div>
            <div className="text-muted">{linked ? (emp.team ?? 'No project team') : 'Not linked to this project'}</div>
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (emp) => <StatusBadge status={emp.status} label={emp.statusLabel} compact />,
    },
    {
      key: 'work',
      header: 'Current work',
      render: (emp) => (
        <div className="text-xs">
          <div className="text-body">{emp.currentChange ?? <span className="text-muted">—</span>}</div>
          <div className="text-muted">{emp.currentStep}</div>
        </div>
      ),
    },
    {
      key: 'blocker',
      header: 'Blocker',
      render: (emp) =>
        emp.blockerShort ? (
          <span className="inline-flex items-center gap-1 text-xs text-danger">
            <AlertTriangle size={12} />
            {emp.blockerShort}
          </span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      key: 'pass',
      header: 'Pass rate',
      sortable: true,
      sortValue: (emp) => emp.passRate,
      render: (emp) => {
        const tone = perfTone(emp.passRate);
        const color = tone === 'bad' ? 'text-danger' : tone === 'warn' ? 'text-warning' : 'text-body';
        return (
          <span className="inline-flex items-center gap-2">
            <PerfBar value={emp.passRate} />
            <span className={`font-mono text-xs ${color}`}>{Math.round(emp.passRate * 100)}%</span>
          </span>
        );
      },
    },
    {
      key: 'cost',
      header: 'Cost, 7 days',
      align: 'right',
      sortable: true,
      sortValue: (emp) => emp.cost7d,
      render: (emp) => (
        <span className="font-mono text-xs text-body">{emp.cost7d > 0 ? `$${emp.cost7d.toFixed(2)}` : '—'}</span>
      ),
    },
    {
      key: 'active',
      header: 'Last active',
      align: 'right',
      render: (emp) => <span className="font-mono text-xs text-muted">{emp.lastActivity}</span>,
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={employees}
      rowKey={(emp) => emp.id}
      onRowClick={(emp) => onOpen(emp.id)}
      getRowAttention={(emp) => (emp.attn || emp.status === 'blocked' ? 'danger' : undefined)}
      loading={loading}
      empty={empty}
      pageSize={pageSize}
      aria-label="Employees"
    />
  );
};
